import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils.js';
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LAYERS } from 'emergency_data/src/components/Map/useDeckGL/Axon/useAxonGLNext.js';
import PropTypes from 'prop-types';
import Portal from '@rsos/base-components/Portal';
import { COLLABORATOR_CAPABILITY } from '@rsos/capstone/src/constants/capabilities';
import { ANI_ALI } from '@rsos/components/capstone/Location/constants/dataSources';
import {
  getDataSource,
  isSourceAniAli,
} from '@rsos/components/capstone/Location/helpers';
import { Dropdown } from '@rsos/components/capstone/base';
import { DropdownBody } from '@rsos/components/capstone/base/Dropdown/DropdownComponents';
import {
  hidePinnedPopover,
  showPinnedPopover,
} from '@rsos/components/capstone/common/Map/useDeckGL/MapPopover/mapPopoverSlice';
import { EMERGENCY_CALLS } from '@rsos/constants/incidentTypes';
import { useFlag } from '@rsos/flags';
import useHasCapability from '@rsos/hooks/useHasCapability';
import useIsTF911Enabled from '@rsos/hooks/useIsTF911Enabled';
import useWhichReverseGeocodeService from '@rsos/hooks/useWhichReverseGeocodeService';
import { GOOGLE, ESRI } from '@rsos/maps/constants/mapProviders';
import {
  ESRI_GEOCODE_SERVICE_URL,
  reverseGeocodeLocationToAddress,
} from '@rsos/maps/interface/reverseGeocode/helpers';
import { selectGeocodeService } from '@rsos/reduxDataStore';
import { viewAllAlerts } from '@rsos/reduxDataStore/src/features/alerts/incidentsSlice';
import { getRTTCollaboratorData } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import {
  viewAllCalls,
  setSelectedCallerID,
  resetNumberWithNewLocation,
} from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import {
  setSelectedLocation,
  clearSelectedLocation,
} from '@rsos/reduxDataStore/src/features/gisSearch/gisSearchSlice';
import getSuggestedAddresses, {
  getAddressFromMagicKey,
} from '@rsos/reduxDataStore/src/features/gisSearch/helpers';
import {
  AGENT_511_MAP_CARD,
  AVIVE_APP_LAUNCHER_MAP_CARD,
  RADE_MAP_CARD,
  setCardsToRender,
  setMapCardData,
  closeCard,
  AXON_MAP_CARD,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import { useLazyGetConversationMetadataQuery } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Api';
import {
  getWhat3WordsLocation,
  resetWhat3WordsLocation,
  resetWhat3WordsLocationError,
} from '@rsos/reduxDataStore/src/features/what3words/what3wordsSlice';
import '@rsos/sinatra';
import { setIRPVersion } from '@rsos/sinatra';
import { getUrlParameter, formatPhoneWithDashes } from '@rsos/utils';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import { notifyError } from '@rsos/utils/toastUtils';
import useCenterPoint from '@rsos/utils/useCenterPoint';
import useCenterPointCurrentView from '@rsos/utils/useCenterPointCurrentView';
import useDebounce from '@rsos/utils/useDebounce';
import useIsIRP from '@rsos/utils/useIsIRP';
import { isValidPhone } from '@rsos/utils/validators';
import { clearLocationError, fetchLocation } from '../../../actions';
import { FETCH_LOCATION_SUCCESS } from '../../../actions/actionTypes';
import { createDatasetsSelector, search } from '../../../utils/search';
import { CALLS, AXON } from '../../../utils/search/constants';
import { show as showRADE } from '../RADECard/radeSlice';
import { isExtraInfoOrMeta, isValidPhoneNumber } from '../helpers';
import AdvancedSearchOptions from './AdvancedSearchOptions';
import {
  SearchBarWrapper,
  InputStyleWrapper,
} from './ConnectedUniversalSearch.styles';
import { ResultsContainer } from './ResultsContainer';
import { SearchDropdownMenu } from './SearchDropdownMenu';
import UniversalSearch from './UniversalSearch';
import { SearchBarInnerWrapper } from './UniversalSearch.styles';
import { trackClickOnSuggestedLocation } from './gisSearchTracking';
import { hasErrorHelper } from './hasErrorHelper';
import {
  w3wInputHelper,
  isW3WSearchTerm,
  isValidADRSearchTerm,
  isADRSearchTerm,
  isLatLongSearchTerm,
  normalizeReverseGeocodeResponse,
} from './inputHelper';
import { reset } from './searchSlice';
import {
  trackQueryByW3W,
  trackQueryByMAFID,
  trackClickOnSearchResult,
  trackQueryFromSearchBar,
} from './universalSearchTrackings';

// Create the datasets selector outside the component to avoid recreating it
const datasetsSelector = createDatasetsSelector([CALLS, AXON]);

// characters to be removed from search term
export const IGNORED_SPECIAL_CHARACTERS = /[-+()\s]/g;

const ConnectedUniversalSearch = ({
  filteredCalls,
  hasManageAccess,
  isInternational,
  productToken,
  setShowW3wCallPane,
  mafError,
  setMafError,
  adrError,
  setADRError,
  searchTerm,
  prevSearchTerm,
  searchType,
  setSearchTerm,
  setSearchType,
  userRoleName,
  isPremiumUser,
  hasMAaccess,
  what3words,
  numberWithNewLocation,
  selectedCallerID,
  locationError,
  hasGisSearchAccess,
  setActiveQueueTab,
}) => {
  const searchBarRef = useRef();
  const resultContainerRef = useRef();
  const dispatch = useDispatch();
  const { queryURI, irpVersion } = useIsIRP();

  const geocodeService = useSelector(selectGeocodeService);

  const queryCallerId = getUrlParameter('caller_id') || '';
  const isTF911Enabled = useIsTF911Enabled();
  const orgName = useSelector(state => state.sinatra.user.currentOrg.name);
  const [triggerGetConversationMetadata] =
    useLazyGetConversationMetadataQuery();

  useEffect(() => {
    setSearchTerm(queryCallerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isValidW3W, setIsValidW3W] = useState(true);
  const [isClickedFromFilter, setIsClickedFromFilter] = useState(false);
  const [isDropdownClicked, setIsDropdownClicked] = useState(false);
  const [isDropdownTriggeredByKey, setIsDropdownTriggeredByKey] =
    useState(false);

  const [mouseCursor, setMouseCursor] = useState(0);
  const [availableSearchOptions, setAvailableSearchOptions] = useState([]); // this is for advanced search
  const what3wordsError = what3words?.error?.status;
  const mapCardData = useSelector(state => state.mapCard.mapCardData);
  const mapCards = useSelector(state => state.mapCard.cards);
  const minLength = 4; // start filtering when user type to 4 characters

  const [isLoading, setIsLoading] = useState(false);
  const [gisSearchResults, setGisSearchResults] = useState([]);
  const [filteredCallResults, setFilteredCallResults] = useState([]);
  const [filteredOutboundSMSResults, setFilteredOutboundSMSResults] = useState(
    [],
  );
  const [filteredAxonResults, setFilteredAxonResults] = useState([]);
  const conversations = useSelector(state => state.textFrom911.conversations);
  const hasCollaborator = useHasCapability(
    COLLABORATOR_CAPABILITY,
  ).hasCapability;

  const service = useWhichReverseGeocodeService();

  /**
   * Use the stable datasets selector created outside the component
   */
  const searchDatasets = useSelector(datasetsSelector);
  const selectedLocation = useSelector(
    state => state.gisSearch.selectedLocation,
  );
  const gisError = useMemo(() => {
    return (
      !isLoading &&
      !gisSearchResults.length &&
      !filteredCallResults.length &&
      !filteredAxonResults.length &&
      (adrError ||
        (mapCards[RADE_MAP_CARD] &&
          !!getMostRecentLoc(filteredCalls[selectedCallerID])))
    );
  }, [
    mapCards,
    adrError,
    filteredCalls,
    selectedCallerID,
    isLoading,
    gisSearchResults,
    filteredCallResults,
    filteredAxonResults,
  ]);

  const isCenterViewPortEnabled = useFlag('center_view_port');
  const jurisdictionCenter = useCenterPoint();
  const mapViewCenter = useCenterPointCurrentView();

  // Use map view center if flag is enabled, otherwise fall back to jurisdiction center
  const { latitude, longitude } = isCenterViewPortEnabled
    ? {
        latitude: mapViewCenter.latitude ?? jurisdictionCenter.latitude,
        longitude: mapViewCenter.longitude ?? jurisdictionCenter.longitude,
      }
    : jurisdictionCenter;

  const getAvailableSearchOptions = () => {
    const w3wOption = {
      name: 'Search W3W',
      prefix: '///',
    };
    const adrOption = {
      name: 'Search additional data by phone number',
      prefix: '/',
    };

    let searchOptions = [adrOption, w3wOption];

    if (hasMAaccess) {
      const option = {
        name: 'Search MedicAlert ID',
        prefix: '/maf',
      };
      searchOptions = [...searchOptions, option];
    }
    setAvailableSearchOptions(searchOptions);
  };

  // This block is for IRP v1 only
  useEffect(() => {
    getAvailableSearchOptions();

    // check if it is IRP
    if (isValidPhone(getUrlParameter('caller_id'))) {
      let callerId = getUrlParameter('caller_id');
      if (callerId) {
        dispatch(setIRPVersion('1'));
        if (irpVersion === '1') {
          searchLocation(callerId);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [irpVersion]);

  // IRP v2
  useEffect(() => {
    if (queryURI && !queryURI.includes('alert')) {
      const irpQuery = queryURI.split('://')[1];
      searchLocation(irpQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryURI]);

  useEffect(() => {
    if (searchType) {
      const updatedSearchterm = `${searchType} ${searchTerm}`;
      setSearchTerm(updatedSearchterm);
      searchBarRef.current.focus();
    } else {
      if (prevSearchTerm !== searchTerm) {
        setSearchTerm(searchBarRef?.current?.value || '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchType]);

  const debouncedFilteredCalls = useDebounce(
    (searchTerm, searchDatasets) => {
      const results =
        search(
          searchTerm.replace(IGNORED_SPECIAL_CHARACTERS, ''),
          searchDatasets,
        ) || [];
      const normalizedCallsResults = results.filter(result => {
        if (filteredCalls[result.data._callId]) {
          return getMostRecentLoc(result.data);
        }
        return false;
      });
      setFilteredCallResults(normalizedCallsResults);
      const normalizedAxonResults = results.filter(
        result =>
          !normalizedCallsResults.includes(result) &&
          result.data?.source === AXON,
      );
      setFilteredAxonResults(normalizedAxonResults);
      // If outbound SMS feature is enabled, filter the conversations to find any outbound SMS
      // messages that match the search term but are not already included in the call results.
      if (isTF911Enabled) {
        const normalizedOutboundSMSResults = Object.entries(conversations)
          .filter(([callerID, conversation]) => {
            if (conversation.data) {
              return (
                !normalizedCallsResults.find(
                  call => call.data._callId === callerID,
                ) && callerID.includes(searchTerm)
              );
            }
            return false;
          })
          .map(([callerID, conversation]) => ({
            ...conversation,
            data: {
              ...conversation.data,
              callerID,
            },
          }));
        setFilteredOutboundSMSResults(normalizedOutboundSMSResults);
      }
    },
    250,
    [filteredCalls, conversations],
  );

  const debouncedGetSuggestedAddress = useDebounce(
    async ({ searchTerm, latitude, longitude, serviceUrl, token }) => {
      if (!isValidPhoneNumber(searchTerm) && !searchTerm.startsWith('/')) {
        await dispatch(
          getSuggestedAddresses?.initiate({
            searchTerm: searchTerm.toLowerCase(),
            latitude,
            longitude,
            serviceUrl,
            token,
          }),
        )
          ?.then(resp => {
            setGisSearchResults(resp?.data || []);
          })
          ?.finally(() => {
            setIsLoading(false);
          });
      } else {
        setGisSearchResults([]);
      }
    },
    500,
    [],
  );

  useEffect(() => {
    setIsLoading(true);
    setGisSearchResults([]);
    setFilteredCallResults([]);
    setFilteredOutboundSMSResults([]);
    if (
      searchTerm &&
      searchTerm.length >= minLength &&
      !searchTerm.includes('/maf') &&
      !isW3WSearchTerm(searchTerm) &&
      !isADRSearchTerm(searchTerm) &&
      !isLatLongSearchTerm(searchTerm)
    ) {
      setMouseCursor(0);
      // if selectedLocation.LongLabel is the searchTerm: break
      if (
        selectedLocation &&
        selectedLocation?.attributes?.LongLabel === searchTerm
      ) {
        setGisSearchResults([]);
        return;
      }
      // clear location and popover when user paste a number in
      if (selectedLocation) {
        dispatch(clearSelectedLocation());
        dispatch(hidePinnedPopover());
      }

      if (hasGisSearchAccess) {
        debouncedGetSuggestedAddress({
          searchTerm: searchTerm.trim(),
          latitude,
          longitude,
          serviceUrl: geocodeService?.gis_service_url,
          token: geocodeService?.token || null,
        });
      }
      debouncedFilteredCalls(searchTerm, searchDatasets);
    } else {
      dispatch(clearSelectedLocation());
      dispatch(hidePinnedPopover());

      if (!searchTerm) {
        setIsDropdownTriggeredByKey(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    // update result dropdown when new call comes in and matches searchTerm
    if (
      searchTerm &&
      searchTerm.length >= minLength &&
      !searchTerm.includes('/maf') &&
      !isW3WSearchTerm(searchTerm) &&
      !isADRSearchTerm(searchTerm) &&
      !isLatLongSearchTerm(searchTerm)
    ) {
      debouncedFilteredCalls(searchTerm, searchDatasets);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetsSelector]);

  const clearOutError = useCallback(() => {
    dispatch(clearLocationError());
    setIsValidW3W(true);
    dispatch(resetWhat3WordsLocationError());
    setMafError(null);
    setADRError(null);
    if (gisError) {
      dispatch(closeCard(RADE_MAP_CARD));
    }
  }, [dispatch, gisError, setADRError, setMafError]);

  /**
   * Handle address selection from dropdown.
   * The suggestion contains { text, magicKey, _serviceUrl, _token } from the /suggest endpoint.
   * We use the magicKey with the SAME service that generated it (_serviceUrl) to fetch
   * full address details with coordinates from /findAddressCandidates.
   */
  const handleSelectedAddress = async suggestion => {
    const { text, magicKey, _serviceUrl, _token } = suggestion;

    // Show the selected text immediately for better UX
    setSearchTerm(text);

    // Determine if this is a global (ESRI world) or local GIS search based on the service URL
    const isEsriWorld = _serviceUrl === ESRI_GEOCODE_SERVICE_URL;
    const gisSource = isEsriWorld ? 'esri_world' : 'local';

    try {
      // Fetch full address details using magicKey with the same service that generated it
      const result = await dispatch(
        getAddressFromMagicKey.initiate({
          magicKey,
          text,
          _serviceUrl,
          _token,
        }),
      );

      const loc = result?.data;

      if (loc) {
        // Local GIS coordinates may need web mercator conversion
        if (!isEsriWorld) {
          let normalizedLoc;
          const [longitude, latitude] = webMercatorUtils.xyToLngLat(
            loc.location.x,
            loc.location.y,
          );
          normalizedLoc = { ...loc, location: { x: longitude, y: latitude } };
          dispatch(setSelectedLocation(normalizedLoc));
          setSearchTerm(normalizedLoc.attributes?.LongLabel || text);
          dispatch(showPinnedPopover({ loc: normalizedLoc }));
        } else {
          dispatch(setSelectedLocation(loc));
          setSearchTerm(loc.attributes?.LongLabel || text);
          dispatch(showPinnedPopover({ loc }));
        }

        const gisType = loc.attributes?.Addr_type;
        trackClickOnSuggestedLocation(irpVersion, gisSource, gisType);
      }
    } catch (error) {
      // If fetching fails, keep the suggestion text displayed
      console.error('Failed to fetch address details:', error);
    }

    clearOutError();
  };

  const previousSelectedCallerIDRef = useRef(selectedCallerID);
  useEffect(() => {
    const prev = previousSelectedCallerIDRef.current;
    if (prev !== selectedCallerID) {
      clearOutError();
    }
    previousSelectedCallerIDRef.current = selectedCallerID;
  }, [selectedCallerID, clearOutError]);

  useEffect(() => {
    if (numberWithNewLocation !== null) {
      const irpQuery = queryURI ? queryURI.split('://')[1] : '';
      const isManualSearch = filteredCalls[numberWithNewLocation]
        ? filteredCalls[numberWithNewLocation].isManualSearch
        : false;
      if (
        (isManualSearch || irpQuery === numberWithNewLocation) &&
        locationError === null
      ) {
        dispatch(setSelectedCallerID(numberWithNewLocation));
      }
      if (numberWithNewLocation === selectedCallerID) {
        clearOutError();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numberWithNewLocation]);

  const formatDisplayPhoneNumber = value => {
    const rg = /^[0-9\s-]+$/i; // number and dashes
    if (rg.test(value)) {
      //replace all dashes
      let numValue = value.replace(/-/g, '');
      if (numValue[0] === '1') {
        if (numValue.length === 11) {
          return formatPhoneWithDashes(numValue);
        }
      } else if (numValue.length === 10) {
        // North american phone number
        const rexp = /^(^[2-9]{1}\d{9})$/;
        if (rexp.test(numValue)) {
          return `1-${formatPhoneWithDashes(numValue)}`;
        }
      }
    }

    return value;
  };

  /**
   * Reset universal search box, clear all user input
   */
  const handleReset = useCallback(() => {
    searchBarRef.current.value = '';
    setMouseCursor(0);
    clearOutError();
    setSearchTerm('');
  }, [clearOutError, setSearchTerm]);

  const handleSearchResult = callerID => {
    const locations = filteredCalls[callerID]?.core?.locations;
    const firstLocIdx = locations?.length - 1 || 0;
    try {
      // when searching phone number without source, hitting 'enter' crashes.
      // this is a temporary fix to prevent the crash
      // TODO: JGAO to look into why this is happening
      const { payloadID, requestID } = isExtraInfoOrMeta(
        locations[firstLocIdx],
      );
      dispatch(setSelectedCallerID(callerID));
      trackClickOnSearchResult(irpVersion, payloadID, requestID);
      handleReset();
      setActiveQueueTab(EMERGENCY_CALLS);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error searching result', error);
    }
  };

  const handleSearchOutboundSMS = useCallback(
    callerID => {
      dispatch(setSelectedCallerID(callerID));
      handleReset();
      setActiveQueueTab(EMERGENCY_CALLS);
    },
    [dispatch, handleReset, setActiveQueueTab],
  );

  const handleResetToAllCalls = () => {
    dispatch(viewAllCalls());
  };

  const handleSearch = searchTerm => {
    if (
      selectedLocation &&
      selectedLocation.attributes.LongLabel === searchTerm
    ) {
      // prevent onSubmit from being called when selecting a suggestedAddress via key down
      return;
    }
    dispatch(resetNumberWithNewLocation());
    dispatch(setCardsToRender(RADE_MAP_CARD));
    dispatch(setMapCardData(null));
    if (searchTerm.includes('/maf')) {
      // The term comes with the searchType, so extract
      let searchType = '/maf';
      let cleanTerm = '';
      const splitQuery = searchTerm.split(searchType);
      if (splitQuery && splitQuery.length > 1) {
        cleanTerm = splitQuery[1].trim();
      }
      trackQueryByMAFID(irpVersion);
      handleResetToAllCalls();
      setSearchTerm(`${searchType} ${cleanTerm}`);
      dispatch(showRADE());
    } else if (isW3WSearchTerm(searchTerm)) {
      let searchType = '///';
      const w3w = w3wInputHelper(searchType, searchTerm.trim());
      if (w3w) {
        setSearchTerm(`${searchType} ${w3w}`);
        dispatch(getWhat3WordsLocation(w3w));
        dispatch(viewAllAlerts());
        const mostRecentLocation = getMostRecentLoc(
          filteredCalls[selectedCallerID],
        );
        if (
          (!selectedCallerID || (selectedCallerID && !mostRecentLocation)) &&
          !what3wordsError
        ) {
          setShowW3wCallPane(true);
        }
        trackQueryByW3W(irpVersion);
      } else {
        setIsValidW3W(false);
      }
    } else if (isADRSearchTerm(searchTerm)) {
      const ADR = isValidADRSearchTerm(searchTerm);
      if (ADR) {
        let searchType = '/';
        handleResetToAllCalls();
        const formattedNumber = isInternational
          ? ADR
          : formatDisplayPhoneNumber(ADR);
        setSearchTerm(`${searchType} ${formattedNumber}`);
        dispatch(showRADE());
      } else {
        setADRError(true);
      }
    } else if (isLatLongSearchTerm(searchTerm)) {
      const latLong = isLatLongSearchTerm(searchTerm);
      if (latLong) {
        const location = {
          latitude: latLong[0],
          longitude: latLong[1],
          service,
          serviceUrl: geocodeService?.gis_service_url,
          token: geocodeService?.token || null,
        };

        reverseGeocodeLocationToAddress(location)
          .then(response => {
            const normalizedResponse = normalizeReverseGeocodeResponse(
              response,
              location.latitude,
              location.longitude,
            );
            dispatch(setSelectedLocation(normalizedResponse));
            dispatch(
              showPinnedPopover({
                loc: normalizedResponse,
              }),
            );
          })
          .catch(error => {
            // eslint-disable-next-line no-console
            console.log('error', error);
          });
      }
    } else if (searchTerm && searchType !== '/maf') {
      const rg = /^[0-9\s-]+$/i; // number and dashes
      /* trim was added to prevent displaying the error message on empty string */
      let callerID = searchTerm.trim();

      if (rg.test(callerID)) {
        if (!isInternational) {
          if (searchTerm.substring(0, 1) === '1') {
            callerID = searchTerm.replace(/[^\d]/g, '');
          } else {
            callerID = `1${searchTerm.replace(/[^\d]/g, '')}`;
          }
        } else {
          callerID = searchTerm.replace(/[^\d]/g, '');
        }
      }
      searchLocation(callerID);
      dispatch(resetWhat3WordsLocation());
      dispatch(showRADE());
    }
  };

  /**
   * Handle Axon search result
   * @param {Object} coordinates - The coordinates of the Axon
   * @param {Object} axon - The Axon object
   * @returns {void}
   */
  const layerData = useSelector(
    state => state.schemas.byId[LAYERS.AXON_BODY_3],
  );

  const handleAxonSearchResult = ({ coordinates, axon }) => {
    try {
      const mapProvider = window?.__RSOS?.map?.mapProvider;
      const map = window?.__RSOS?.map?.mapInstance;
      if (mapProvider === GOOGLE) {
        map?.panTo?.({ lat: coordinates[1], lng: coordinates[0] });
        map?.setZoom?.(17);
      } else if (mapProvider === ESRI) {
        if (map?.view) {
          map.view.zoom = 16;
          map.view.center = coordinates;
        }
      }

      dispatch(
        setMapCardData({
          layerData,
          spatialObject: axon,
        }),
      );
      dispatch(setCardsToRender(AXON_MAP_CARD));

      handleReset();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error panning to Axon coordinates', error);
    }
  };

  /**
   * Redirect user to /query-interface if the search term doesn't match the
   * caller_id query param
   * @param {String} callerID - the searchTerm in SearchBar
   */
  // TODO FALCON CHECK IF THIS IS STILL NEEDED
  // const handleSearch = callerID => {
  //   if (callerID !== getUrlParameter('caller_id')) {
  //     //history.push('/query-interface');
  //   }
  // };

  /**
   * Legacy query view manual search location
   * callerID query param
   * @param {String} callerID - the search caller id in SearchBar
   */
  function searchLocation(callerID) {
    if (!isClickedFromFilter) {
      // limit to only for QV, not from clicking from the filter list
      let token = productToken || localStorage.getItem('productToken');

      if (callerID) {
        if (token) {
          if (isInternational || isValidPhone(callerID)) {
            setSearchTerm(callerID);
            dispatch(fetchLocation(callerID, token))
              .then(res => {
                if (
                  res?.action &&
                  res?.action?.type === FETCH_LOCATION_SUCCESS
                ) {
                  const { requestID, payloadID } = res;
                  const mostRecentLocation = getMostRecentLoc(
                    filteredCalls[callerID],
                  );
                  const isAniAli = isSourceAniAli(mostRecentLocation);
                  const serviceType = isAniAli
                    ? ANI_ALI
                    : getDataSource(mostRecentLocation);
                  const classOfServices = isAniAli
                    ? mostRecentLocation?.class_of_service
                    : null;

                  trackQueryFromSearchBar(
                    userRoleName,
                    irpVersion,
                    payloadID,
                    requestID,
                    serviceType,
                    classOfServices,
                  );
                }
                // Check for existing text from 911 conversation metadata if TF911 is enabled
                if (isTF911Enabled && orgName) {
                  if (isInternational) {
                    const digitCount = callerID.replace(/\D/g, '').length;
                    if (
                      /[^0-9+-]/.test(callerID) ||
                      digitCount < 10 ||
                      digitCount > 15
                    ) {
                      setADRError(true);
                      if (searchBarRef?.current?.focus) {
                        searchBarRef.current.focus();
                      }
                      return;
                    }
                  }
                  triggerGetConversationMetadata({
                    callerID: callerID,
                    orgName,
                    custom: { isOutboundSMS: true },
                  });
                }
              })
              .then(() => {
                if (hasCollaborator) {
                  dispatch(getRTTCollaboratorData({ callerID, orgName })); // check for collaborator perms and capability
                }
              })
              .finally(() => {
                dispatch(setSelectedCallerID(callerID));
              });
            if (!isInternational) {
              searchBarRef.current.value = formatDisplayPhoneNumber(callerID);
            } else {
              searchBarRef.current.value = callerID;
            }
          } else {
            searchBarRef.current.focus();
            const mostRecentLocation = getMostRecentLoc(
              filteredCalls[selectedCallerID],
            );
            if (!hasGisSearchAccess && selectedCallerID && mostRecentLocation) {
              setADRError(true);
            }
          }
        } else if (!token && hasManageAccess) {
          notifyError(
            "Your agency's jurisdiction has not been approved yet. Approval can take up to 15 days.",
          );
        } else if (!token && !hasManageAccess) {
          const errMsg = () => {
            return (
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    'An error occurred, please try again.' +
                    ' If this error persists, submit a ticket via the "Support" tab.',
                }}
              ></div>
            );
          };
          notifyError({ message: errMsg() });
        }
        // format US display number only
        if (!isInternational) {
          setSearchTerm(formatDisplayPhoneNumber(searchTerm));
        }
      } else if (!callerID && searchTerm) {
        dispatch(reset());
      }
    }
  }

  const closeDropdown = onClick => {
    setIsDropdownClicked(false);
    onClick();
  };

  //TODO: Back trace these errors and remove unused ones from the app
  const { hasError, displayResultContainer } = hasErrorHelper({
    hasGisSearchAccess,
    searchTerm,
    minLength,
    searchType,

    gisError,
    adrError,
    mafError,
    what3wordsError,
    locationError, // not actually being used but kept for parity
    isValidW3W,

    selectedLocation,
  });

  useEffect(() => {
    // Reset the search if any map card has been opened
    // and selectedLocation is falsey
    if (
      !selectedLocation &&
      !gisSearchResults.length &&
      !filteredCallResults.length &&
      !filteredAxonResults.length &&
      ((mapCardData && Object.keys(mapCardData).length) ||
        mapCards[AVIVE_APP_LAUNCHER_MAP_CARD] ||
        mapCards[AGENT_511_MAP_CARD])
    ) {
      dispatch(reset());
      clearOutError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, mapCardData, mapCards]);

  const searchBarWrapperRef = useRef();

  const [resultsDropdownPosition, setResultsDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 264,
  });

  useEffect(() => {
    if (displayResultContainer && searchBarWrapperRef.current) {
      const rect = searchBarWrapperRef.current.getBoundingClientRect();
      setResultsDropdownPosition({
        top: rect.bottom - 2,
        left: rect.left + 12,
        width: rect.width - 24,
      });
    }
  }, [displayResultContainer]);

  return (
    <>
      <SearchBarWrapper ref={searchBarWrapperRef}>
        <SearchBarInnerWrapper hasError={hasError}>
          <Dropdown
            zIndex={3}
            triggerElement={({ onClick, selected }) => (
              <SearchDropdownMenu
                onClick={onClick}
                selected={selected}
                searchTerm={searchTerm}
                isDropdownClicked={isDropdownClicked}
                isDropdownTriggeredByKey={isDropdownTriggeredByKey}
                onDropdownClicked={setIsDropdownClicked}
                onDropdownTriggeredByKey={setIsDropdownTriggeredByKey}
                setSearchTerm={setSearchTerm}
                clearOutError={clearOutError}
              />
            )}
            triggerOnClose
          >
            {(onDropdownClose, dropdownPosition) => (
              <DropdownBody>
                <AdvancedSearchOptions
                  availableSearchOptions={availableSearchOptions}
                  onSearchTypeChange={setSearchType}
                  closeDropdown={() => closeDropdown(onDropdownClose)}
                  top={dropdownPosition?.top + 14}
                  left={dropdownPosition?.left - 1}
                  width={dropdownPosition?.width}
                />
              </DropdownBody>
            )}
          </Dropdown>
          <InputStyleWrapper hasError={hasError}>
            <UniversalSearch
              clearOutError={clearOutError}
              handleSearchResult={handleSearchResult}
              handleSearchOutboundSMS={handleSearchOutboundSMS}
              hasError={hasError}
              mouseCursor={mouseCursor}
              searchBarRef={searchBarRef}
              searchLocation={searchLocation}
              searchTerm={searchTerm}
              searchType={searchType}
              setIsClickedFromFilter={setIsClickedFromFilter}
              setMouseCursor={setMouseCursor}
              setSearchTerm={setSearchTerm}
              onReset={handleReset}
              onSearch={handleSearch}
              handleSelectedAddress={handleSelectedAddress}
              resultContainerRef={resultContainerRef}
              gisSearchResults={gisSearchResults}
              filteredCallResults={filteredCallResults}
              filteredAxonResults={filteredAxonResults}
              filteredOutboundSMSResults={filteredOutboundSMSResults}
              handleAxonSearchResult={handleAxonSearchResult}
            />
          </InputStyleWrapper>
        </SearchBarInnerWrapper>
      </SearchBarWrapper>
      {displayResultContainer && (
        <Portal id="universal-search-results-portal" priority={1}>
          <ResultsContainer
            displayResultContainer={displayResultContainer}
            hasError={hasError}
            isValidW3W={isValidW3W}
            mouseCursor={mouseCursor}
            isInternational={isInternational}
            formatPhoneWithDashes={formatPhoneWithDashes}
            handleSearchResult={handleSearchResult}
            handleSearchOutboundSMS={handleSearchOutboundSMS}
            isPremiumUser={isPremiumUser}
            hasGisSearchAccess={hasGisSearchAccess}
            handleSelectedAddress={handleSelectedAddress}
            resultContainerRef={resultContainerRef}
            gisSearchResults={gisSearchResults}
            filteredCallResults={filteredCallResults}
            filteredAxonResults={filteredAxonResults}
            filteredOutboundSMSResults={filteredOutboundSMSResults}
            top={resultsDropdownPosition.top}
            left={resultsDropdownPosition.left}
            width={resultsDropdownPosition.width}
            handleAxonSearchResult={handleAxonSearchResult}
          />
        </Portal>
      )}
    </>
  );
};

ConnectedUniversalSearch.propTypes = {
  filteredCalls: PropTypes.object.isRequired,
  hasManageAccess: PropTypes.bool.isRequired,
  isInternational: PropTypes.bool.isRequired,
  productToken: PropTypes.string.isRequired,
  selectedCallerID: PropTypes.string,
  userRoleName: PropTypes.string.isRequired,
};

export default ConnectedUniversalSearch;
